import { default as DOMPurify } from 'dompurify';
import isEmpty from 'lodash-es/isEmpty';
import trim from 'lodash-es/trim';
import { z } from 'zod';

export class StringUtils {
  static withPluralize = (count: number, wordSingle: string, wordMultiple = `${wordSingle}s`) =>
    count === 1 ? `${count} ${wordSingle}` : `${count} ${wordMultiple}`;

  static withFallback = (value: string | undefined | null, fallback = '-') => {
    try {
      return value || fallback;
    } catch (_error) {
      return fallback;
    }
  };

  static isUrl = (value: string | null | undefined): boolean => {
    let url;
    try {
      url = new URL(value || '');
    } catch (_) {
      return false;
    }

    const hosts = url.host.split('.').filter(it => it.trim().length > 0);

    return (
      hosts.length >= 2 &&
      hosts[hosts.length - 1].length >= 2 &&
      (url.protocol === 'http:' || url.protocol === 'https:')
    );
  };

  static toHTMLAnchor = (value: string | null | undefined) => {
    if (!value) {
      return value || '';
    }

    // Zod transform for URL/Email
    const zodUrl = z
      .string()
      .url()
      .transform(val => `<a class="break-all hover:underline" href="${val}" target="_blank">${val}</a>`);
    const zodEmail = z
      .string()
      .email()
      .transform(val => `<a class="break-all hover:underline" href="mailto:${val}" target="_blank">${val}</a>`);

    // noinspection HtmlUnknownTarget
    return DOMPurify.sanitize(
      value
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replace(/(https?:\/\/\S+)/gi, val => zodUrl.safeParse(val).data || val)
        .replace(/(\S+@\S+\.\w{2,})/gi, val =>
          val
            .split(',')
            .map(v => zodEmail.safeParse(v).data || v)
            .join(','),
        ),
      { ADD_TAGS: ['a'], ADD_ATTR: ['target'] },
    );
  };

  static emptyToNull = (value: string | null | undefined): string | null => {
    if (value === null || typeof value === 'undefined') {
      return null;
    } else if (isEmpty(trim(value))) {
      return null;
    } else {
      return value;
    }
  };

  static capitalize = (value: string) => {
    if (value.length === 0) return '';
    if (value.length === 1) return value.toUpperCase();
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };
}
